import { Button, Flex, Form, Input, message } from 'antd'
import { useAttemptListener } from 'dev-masters-react-kit'
import { useEffect, useState } from 'react'
import { ActivityCalendar } from 'react-activity-calendar'
import 'react-activity-calendar/tooltips.css'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { attemptGetListeningStatistics } from '../../utils/redux/statisticsSlice'
import {
  listenerMiddleware,
  useAppDispatch,
  useAppSelector
} from '../../utils/redux/store'
import {
  attemptBan,
  attemptDelete,
  attemptUnban,
  attemptValidate
} from '../../utils/redux/usersSlice'

const formatDuration = (totalSeconds: number): string => {
  if (!totalSeconds) return '0m'; // Handle undefined or 0

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  // Formatting logic
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
};


//"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI2N2E0ODYzZTk5M2E4ZmMwNGVlNGYxMjQiLCJlbWFpbCI6ImRldkBkZXYuY29tIiwibmFtZSI6Ik5ldyB1c2VyIiwiaWF0IjoxNzM4ODM1NTE4LCJleHAiOjE3NzAzNzE1MTh9.WY52aZqrsaVs5xkTRuhZDmYdM6LSfHG95YLezFFrlSI"
//dev@dev.com
//dev@dev.com
export default function ModifyClient() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const useId = useParams().id
  const user = useAppSelector((state) => state.user.users[useId || ''])

  function validate() {
    dispatch(attemptValidate(user._id))
  }

  function ban() {
    dispatch(attemptBan(user._id))
  }

  function unban() {
    dispatch(attemptUnban(user._id))
  }

  function deleteUser() {
    dispatch(attemptDelete(user._id))
  }

  const validateLoading = useAttemptListener({
    listenerMiddleware: listenerMiddleware,

    attempt: attemptValidate,
    onFulfilled: () => {
      message.success('User has been validated successfully!')
      navigate('/users/clients/all')
    },
    onRejected: (action) => message.error(action.error.message),
  })

  const banLoading = useAttemptListener({
    listenerMiddleware: listenerMiddleware,

    attempt: attemptBan,
    onFulfilled: () => {
      message.success('User has been banned successfully!')
      navigate('/users/clients/all')
    },
    onRejected: (action) => message.error(action.error.message),
  })

  const unbanLoading = useAttemptListener({
    listenerMiddleware: listenerMiddleware,

    attempt: attemptUnban,
    onFulfilled: () => {
      message.success('User has been unbanned successfully!')
      navigate('/users/clients/all')
    },
    onRejected: (action) => message.error(action.error.message),
  })

  const deleteLoading = useAttemptListener({
    listenerMiddleware: listenerMiddleware,

    attempt: attemptDelete,
    onFulfilled: () => {
      message.success('User has been deleted successfully!')
      navigate('/users/clients/all')
    },
    onRejected: (action) => message.error(action.error.message),
  })

  if (!user) {
    message.error({content: 'User not found!', key: useId})
    return <Navigate to="/users/clients/all" />
  }

  const [activityData, setActivityData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    async function fetchAndFormatData() {
      try {
        // 1. Fetch the data from your NestJS backend
        const backendData = await dispatch(attemptGetListeningStatistics({ userId: useId || '', year })).unwrap();

        // 2. Transform the data for the library
        let formattedData = [];
        let weeklyDurationAccumulator = 0;

        for (let i = 0; i < backendData.length; i++) {
          const item = backendData[i];
          
          // Use UTC parsing to ensure timezones don't shift the day of the week
          const currentDate = new Date(item.date);
          const dateStr = item.date.split('T')[0]; 

          // Determine the tier level based on totalListens
          let tier = 0;
          if (item.totalListens > 0 && item.totalListens <= 10) tier = 1;
          else if (item.totalListens > 10 && item.totalListens <= 30) tier = 2;
          else if (item.totalListens > 30 && item.totalListens <= 70) tier = 3;
          else if (item.totalListens > 70) tier = 4;

          // Accumulate the duration
          weeklyDurationAccumulator += item.totalDuration ?? 0;

          const formattedItem: any = {
            date: dateStr,
            count: item.totalListens,
            level: tier,
          };

          // --- BOUNDARY CALCULATION ---
          // Find the upcoming Sunday
          const dayOfWeek = currentDate.getUTCDay(); // 0 is Sunday
          const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
          const upcomingSunday = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate() + daysToSunday));
          
          // Find Dec 31st of the requested year
          const endOfYear = new Date(Date.UTC(year, 11, 31)); 

          // The boundary is Sunday OR Dec 31st, whichever happens first
          const weekBoundaryDate = new Date(Math.min(upcomingSunday.getTime(), endOfYear.getTime()));
          const weekBoundaryStr = weekBoundaryDate.toISOString().split('T')[0];

          // Case 1: The current date IS the boundary (It's Sunday or Dec 31st)
          if (dateStr === weekBoundaryStr) {
            formattedItem.weekTotalDuration = weeklyDurationAccumulator;
            weeklyDurationAccumulator = 0; // Reset for the next week
            formattedData.push(formattedItem);
            continue; // Move to the next item
          }

          // If it's not the boundary, push the normal day
          formattedData.push(formattedItem);

          // Case 2: The boundary is MISSING in our data. 
          // We check if the NEXT item exists and skips over our boundary.
          const nextItem = backendData[i + 1];
          let needsBoundaryInjection = false;

          if (!nextItem) {
            // This was the very last item in the array, and we haven't hit the boundary yet
            needsBoundaryInjection = true;
          } else {
            const nextItemDate = new Date(nextItem.date);
            if (nextItemDate.getTime() > weekBoundaryDate.getTime()) {
              // The next item belongs to a future week, meaning we skipped the boundary
              needsBoundaryInjection = true;
            }
          }

          // Inject the missing boundary (Sunday or Dec 31st) to hold the week's total
          if (needsBoundaryInjection) {
            formattedData.push({
              date: weekBoundaryStr,
              count: 0,
              level: 0, // 0 listens = tier 0
              weekTotalDuration: weeklyDurationAccumulator,
            });
            
            // Reset accumulator after injecting
            weeklyDurationAccumulator = 0; 
          }
        }

        if (formattedData.length > 0 && formattedData[0].date != `${year}-01-01`) {
          formattedData.unshift({ date: `${year}-01-01`, count: 0, level: 0 });
        }

        if (formattedData.length > 0 && formattedData[formattedData.length - 1].date != `${year}-12-31`) {
          formattedData.push({ date: `${year}-12-31`, count: 0, level: 0 });
        }

        setActivityData(formattedData);
      } catch (error) {
        console.error("Failed to load activity", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAndFormatData();
  }, [useId, year]);

  return (
    <>
      {!loading && activityData.length === 0 ? (
        <p>No activity data available for this user.</p>
      ) : (
        <ActivityCalendar
          data={activityData}
          loading={loading}
          theme={{
            light: ['hsl(0, 0%, 92%)', 'rebeccapurple'],
          }}
          tooltips={{
            activity: {
              text: (activity) => {
                const a = activity as any; // Type assertion to access weekTotalDuration
                let songsText = `${a.count} songs listened on ${a.date}`;
                let weekText = a.weekTotalDuration ? `. Week total duration: ${formatDuration(a.weekTotalDuration)}.` : '';
                return songsText + weekText;
              },
              withArrow: true, // Optional: Adds a little pointer arrow to the tooltip
            }
          }}
          showColorLegend={true}
          showMonthLabels={true}
          showTotalCount={true}
          weekStart={1}
          style={{ marginBottom: '1em' }}
        />
      )}
      <Form
        initialValues={{
          email: user.email,
          name: user.name,
          city: user.city,
          businessType: user.businessType,
          identifier: user.identifier,
        }}
        disabled={true}
      >
        <Form.Item
          name="name"
          rules={[{ required: true, message: 'Please input customer name!' }]}
        >
          <Input placeholder="Name" />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { required: true, message: 'Please input the email!' },
            { type: 'email', message: 'Invalid email, example: user@gmail.com!' },
          ]}
        >
          <Input placeholder="Email" />
        </Form.Item>
        <Form.Item
          name="city"
          rules={[{ required: true, message: 'Please provide customer city!' }]}
        >
          <Input placeholder="City" />
        </Form.Item>
        <Form.Item
          name="businessType"
          rules={[{ required: true, message: 'Please provide customer business type!' }]}
        >
          <Input placeholder="Business type" />
        </Form.Item>
        <Form.Item
          name="identifier"
          rules={[{ required: true, message: 'Please provide customer identifier!' }]}
        >
          <Input placeholder="Identifier" />
        </Form.Item>
      </Form>
      <Flex justify="space-between">
        <Button type="primary" danger onClick={deleteUser} loading={deleteLoading}>
          Delete user
        </Button>
        {!user.validatedAt && (
          <Button type="primary" onClick={validate} loading={validateLoading}>
            Validate
          </Button>
        )}
        {user.validatedAt &&
          (user.bannedAt ? (
            <Button type="default" onClick={unban} loading={unbanLoading}>
              Unban
            </Button>
          ) : (
            <Button type="default" onClick={ban} loading={banLoading}>
              Ban
            </Button>
          ))}
      </Flex>
    </>
  )
}
